import {
  OfflineError,
  cacheTripBundle,
  ensureInstanceChecklist,
  fetchInstance,
  findMemberInstanceForPlan,
  getCachedTripBundle,
  isOnline,
  loadInstanceData,
  loadTrip,
  setInstanceEditorAccess,
  subscribeInstance,
  upsertTripOnCachedShelf,
} from './store.js';
import { getSession, getUserId } from './supabase-client.js';
import { backfillInstanceCoordsFromUrls, resolveItineraryDays } from './instance-itinerary.js';
import { resolveChecklistItems } from './utils/plan-checklist.js';
import { fetchWithTimeout, setSyncStatus } from './utils/network-fetch.js';
import { buildOfflinePack } from './utils/offline-pack.js';

let context = null;
let unsubscribeRealtime = null;
let onChangeCallback = null;
const refreshingTrips = new Set();

const backfillSessionKey = (instanceId) => `metis-coords-backfill:${instanceId}`;

export function getContext() {
  return context;
}

function bundleToContext(bundle) {
  return {
    tripId: bundle.tripId,
    trip: bundle.trip,
    plan: bundle.plan,
    planDays: bundle.planDays,
    days: bundle.days,
    sections: bundle.sections,
    instance: bundle.instance,
    live: bundle.live,
    checklistItems: bundle.checklistItems ?? resolveChecklistItems(bundle.plan, bundle.instance),
    fromCache: true,
    cachedAt: bundle.cachedAt,
    syncStatus: isOnline() ? 'stale' : 'offline',
  };
}

function contextToBundle(ctx) {
  const { checks, notes, expenses, journal, members, bases, wishlist } = ctx.live;
  return {
    instanceId: ctx.instance.id,
    trip: ctx.trip,
    plan: ctx.plan,
    planDays: ctx.planDays,
    days: ctx.days,
    sections: ctx.sections,
    instance: ctx.instance,
    live: { checks, notes, expenses, journal, members, bases, wishlist },
    checklistItems: ctx.checklistItems,
  };
}

function resolveInstanceEditorAccess(instance, members, userId) {
  if (!userId) return false;
  if (instance.owner_id === userId) return true;
  const member = members?.find((m) => m.user_id === userId);
  if (!member) return false;
  return (member.role ?? 'editor') === 'editor';
}

async function syncEditorAccess(ctx) {
  const session = await getSession();
  const userId = getUserId(session);
  const editorAccess = resolveInstanceEditorAccess(ctx.instance, ctx.live.members, userId);
  setInstanceEditorAccess(editorAccess);
  if (!editorAccess) {
    ctx.live.expenses = [];
  }
}

function applyContext(next) {
  context = {
    ...next,
    fromCache: false,
    cachedAt: new Date().toISOString(),
    syncStatus: 'fresh',
  };
  syncEditorAccess(context).catch(() => setInstanceEditorAccess(true));
}

function setupRealtime(instanceId) {
  unsubscribeRealtime?.();
  unsubscribeRealtime = subscribeInstance(instanceId, async ({ table } = {}) => {
    if (table === 'instances') {
      await refreshChecklistFromNetwork();
    } else {
      await refreshLiveData({ forceNetwork: true });
    }
    onChangeCallback?.();
  });
}

function scheduleOfflinePack(bundle, { force = false } = {}) {
  if (!isOnline()) return;
  const tripId = bundle.tripId ?? bundle.trip?.id;
  if (!tripId) return;

  buildOfflinePack(bundle, { force }).catch(() => {});
}

function scheduleCoordsBackfill(instanceId) {
  if (!navigator.onLine || sessionStorage.getItem(backfillSessionKey(instanceId))) return;

  backfillInstanceCoordsFromUrls(instanceId)
    .then(async (updated) => {
      sessionStorage.setItem(backfillSessionKey(instanceId), '1');
      if (updated > 0) await refreshLiveData({ forceNetwork: true });
    })
    .catch(() => {
      sessionStorage.setItem(backfillSessionKey(instanceId), '1');
    });
}

async function fetchTripContextFromNetwork(tripId, { forceNetwork = false, networkTimeoutMs = 5000 } = {}) {
  const tripData = await fetchWithTimeout(loadTrip(tripId), networkTimeoutMs);
  const instance = await findMemberInstanceForPlan(tripData.plan.id);
  if (!instance) {
    const err = new Error('Avvia il viaggio per continuare.');
    err.code = 'INSTANCE_REQUIRED';
    err.tripId = tripId;
    throw err;
  }

  const live = await loadInstanceData(instance.id, { forceNetwork, networkTimeoutMs });
  const days = await resolveItineraryDays(instance.id, tripData.days);
  const checklistItems = await ensureInstanceChecklist(instance, tripData.plan);

  return {
    tripId,
    trip: tripData.trip,
    plan: tripData.plan,
    planDays: tripData.days,
    days,
    sections: tripData.sections,
    instance: { ...instance, checklist_items: checklistItems },
    live,
    checklistItems,
  };
}

async function refreshTripContextInBackground(tripId) {
  if (refreshingTrips.has(tripId)) return;
  refreshingTrips.add(tripId);
  setSyncStatus('refreshing', context?.cachedAt ?? null);

  try {
    const fresh = await fetchTripContextFromNetwork(tripId, { forceNetwork: true });
    await cacheTripBundle(tripId, contextToBundle(fresh));

    if (context?.tripId === tripId) {
      applyContext(fresh);
      setupRealtime(fresh.instance.id);
      scheduleCoordsBackfill(fresh.instance.id);
      scheduleOfflinePack(fresh);
      setSyncStatus('fresh', context.cachedAt);
      onChangeCallback?.();
    } else {
      setSyncStatus('fresh');
    }
  } catch {
    if (context?.tripId === tripId) {
      context.syncStatus = isOnline() ? 'stale' : 'offline';
      setSyncStatus(context.syncStatus, context.cachedAt ?? null);
      onChangeCallback?.();
    } else {
      setSyncStatus(isOnline() ? 'stale' : 'offline');
    }
  } finally {
    refreshingTrips.delete(tripId);
  }
}

export async function ensureTripContext(tripId) {
  if (context?.tripId === tripId) {
    return context;
  }

  tearDownRealtime();

  const cached = await getCachedTripBundle(tripId);
  if (cached) {
    context = bundleToContext(cached);
    syncEditorAccess(context).catch(() => setInstanceEditorAccess(true));
    setSyncStatus(context.syncStatus, cached.cachedAt);
    if (isOnline()) {
      refreshTripContextInBackground(tripId);
    } else {
      setSyncStatus('offline', cached.cachedAt);
    }
    return context;
  }

  if (!isOnline()) {
    throw new OfflineError('Apri almeno una volta con connessione per salvare i dati offline.');
  }

  try {
    const fresh = await fetchTripContextFromNetwork(tripId, { forceNetwork: true });
    applyContext(fresh);
    await cacheTripBundle(tripId, contextToBundle(context));
    setSyncStatus('fresh', context.cachedAt);
    setupRealtime(fresh.instance.id);
    scheduleCoordsBackfill(fresh.instance.id);
    scheduleOfflinePack(fresh);
    return context;
  } catch (err) {
    throw err;
  }
}

/** Forza il salvataggio del piano in cache locale (pulsante "Scarica piano"). */
export async function downloadTripForOffline(tripId) {
  if (!isOnline()) {
    throw new OfflineError('Serve connessione per scaricare il piano aggiornato.');
  }

  const fresh = await fetchTripContextFromNetwork(tripId, {
    forceNetwork: true,
    networkTimeoutMs: 15000,
  });
  await cacheTripBundle(tripId, contextToBundle(fresh));
  await upsertTripOnCachedShelf(fresh.trip, fresh.plan, fresh.instance.id);

  setSyncStatus('downloading', context?.cachedAt ?? null);
  await buildOfflinePack(fresh, {
    force: true,
    onProgress: ({ phase, done, total }) => {
      if (phase === 'tiles' && done % 40 === 0) {
        setSyncStatus('downloading', context?.cachedAt ?? null);
      }
      if (phase === 'routes' && done === total) {
        setSyncStatus('downloading', context?.cachedAt ?? null);
      }
    },
  });

  if (context?.tripId === tripId) {
    applyContext(fresh);
    setupRealtime(fresh.instance.id);
    setSyncStatus('fresh', context.cachedAt);
    onChangeCallback?.();
  } else {
    setSyncStatus('fresh');
  }
  return fresh;
}

export async function retryTripRefresh() {
  if (!context?.tripId || !isOnline()) return;
  await refreshTripContextInBackground(context.tripId);
}

export async function refreshLiveData({ forceNetwork = false } = {}) {
  if (!context) return;
  context.live = await loadInstanceData(context.instance.id, { forceNetwork });
  if (isOnline()) {
    context.days = await resolveItineraryDays(context.instance.id, context.planDays);
  }
  await syncEditorAccess(context);
  await cacheTripBundle(context.tripId, contextToBundle(context));
}

export async function refreshChecklistFromNetwork() {
  if (!context) return;
  const instance = await fetchInstance(context.instance.id);
  context.instance = instance;
  context.checklistItems = resolveChecklistItems(context.plan, instance);
  await cacheTripBundle(context.tripId, contextToBundle(context));
}

export function patchChecklistItems(items, instancePatch = null) {
  if (!context) return;
  context.checklistItems = items;
  if (instancePatch) {
    context.instance = { ...context.instance, ...instancePatch };
  }
}

export function patchStopCoordsInContext(stopId, coords) {
  if (!context) return;
  for (const day of context.days) {
    for (const stop of day.stops ?? []) {
      if (stop.id === stopId) {
        stop.lat = coords.lat;
        stop.lng = coords.lng;
        return;
      }
    }
  }
}

export function patchBaseCoordsInContext(baseId, coords) {
  if (!context?.live?.bases) return;
  const base = context.live.bases.find((b) => b.id === baseId);
  if (base) {
    base.lat = coords.lat;
    base.lng = coords.lng;
  }
}

export function onTripChange(callback) {
  onChangeCallback = callback;
}

export function tearDownRealtime() {
  unsubscribeRealtime?.();
  unsubscribeRealtime = null;
  context = null;
  setInstanceEditorAccess(true);
}
